import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react'
import AutocompleteQuery, {
    AUTOCOMPLETE_ACTIVITY_TYPE,
    AUTOCOMPLETE_QUERY_TYPE
} from "../../../molecules/autocomplete-query/AutocompleteQuery";
import {GeocodingFeature, GeocodingFeatureProperties} from "@mapbox/search-js-core/dist/geocode/types";
import cloneDeep from "lodash/cloneDeep";
import {
    Button,
    CheckboxInput,
    DelayedSpinner,
    DropdownInput,
    DropdownOption,
    Fieldset,
    Icon,
    IconAndText,
    IconButton,
    NumberInput,
    RadioButtonGroup,
    Spinner,
    SpinnerOverlay,
    Text,
    TextButton,
    TextInput
} from "atmosfair-ui";
import {theme} from "../../../styles/theme";
import {useTranslation} from "react-i18next";
import "./styles.css"
//@ts-ignore
import mapboxgl, {GeoJSONSourceOptions, GeoJSONSourceRaw, LngLat, MapMouseEvent} from 'mapbox-gl';
import {
    apiSlice,
    useCalculateEmissionsMutation,
    useGetCarCalculatorPropsQuery,
    useGetCarPropsViaAcrissCodeMutation,
    useGetRouteFromCoordinatesMutation,
    useValidateAcrissCodeMutation,
    useLazyGetComputedTotalPassengerCountQuery
} from "../../../redux/reducer/api";
import {v4 as uuidv4} from "uuid";
import useOutsideClick from "../../../hooks/use-outside-click";
import {createRoot} from "react-dom/client";
import {SMALL_SPINNER_SIZE} from "../../../utils/common";
import clsx from "clsx";
import {convertCarBusEnumToDropdownFormat, WHICH_CAR_BUS_PROPS} from "../../../utils/converter/dropdown";
import {Tooltip} from "@mui/material";
import {toast, TypeOptions} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {ChooseActivityCommonProps, createErrorCodeString} from "../utils";
import {
    AcrissCodeProps,
    AcrissCodeRequest,
    BUS_CATEGORY,
    CAR_CATEGORY,
    CarBusCalculationRequest,
    FUEL_TYPE,
    VEHICLE_TYPE
} from '../../../api/model/activity-car-bus';
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL, CALCULATION_TYPE} from "../../../api/model/common";
import {ActivityCarBusState, WaypointsProps} from "../../../types-enums/activity-car-bus";
import {
    formatDistanceKmString,
    generateTotalCo2EAndCo2EPerPersonString,
    NO_DATA_STRING
} from "../../../utils/string/common";
import {
    isCalculatedCarBusEmissionsResponse,
    isGeneralCalculatedCarBusEmissionsResponse
} from "../../../utils/check/type-guards";
import {setCommonCarBusActivityProps} from "../../../utils/setter/activities";
import {ActivityCommonProperties, DurationProps} from "../../../types-enums/activity-common-properties";
import {generateCarBusCalculationTierHintString} from "../../../utils/string/car-bus";
import {CalculatedEmissionsBackendResponse} from "../../../api/model/emissions";
import {KG_CO2} from "../../../utils/string/units";
import {GeoJSON, Position} from "geojson";
import {roundKm} from "../../../utils/math/round-values";
import {isEqual} from "lodash";
import usePrevious from "../../../hooks/use-previous";
import {getDurationInDaysOrNights} from "../../../utils/getter/common";
import useFirstRender from "../../../hooks/use-first-render";

const haversine = require('haversine-js');

const WAYPOINTS_LAYER = "waypoints_layer"
const ROUTE_LAYER = "route_layer"
const WAYPOINTS_SOURCE = "waypoints_source"
const ROUTE_SOURCE = "route_source"
const WAYPOINTS_COUNT_LABEL = "waypoints_count"
const MAPBOX_DEM_SOURCE = "mapbox-dem"
const MAPBOX_STREET_STYLE = "mapbox://styles/mapbox/streets-v10"
const STREET_LAYER_NUMBER = 1
const SATELLITE_LAYER_NUMBER = 2
const OUTDOOR_LAYER_NUMBER = 3

interface StyledGeocodingFeatureProperties extends GeocodingFeatureProperties {
    color: "primary" | "secondary" | "black"
    size: "small" | "medium" | "large"
    label: string
}

interface FormProps {
    vehicleType: VEHICLE_TYPE | null,
    fuelType: FUEL_TYPE | null,
    manualEnteredDistanceKm: number | null,
    calculatedDistanceKm: number | null,
    fuelConsumption: number | null
    carCategory: CAR_CATEGORY | null,
    busCategory: BUS_CATEGORY | null,
    totalPassengerCount: number | null,
    acrissCode: string | null,
    countDays: number,
    passengerCount: number,
    waypoints: WaypointsProps,
    route: GeoJSON.LineString | null,
    isHybrid: boolean
}

interface FormErrorProps {
    calculatedDistanceError: boolean,
    manualEnteredDistanceError: boolean,
    fuelConsumptionError: boolean,
    personCountError: boolean
    daysCountError: boolean,
    acrissCodeError: boolean,
    vehicleTypeError: boolean,
    totalPassengerCountError: boolean,
}

interface ChooseCarBusProps extends ChooseActivityCommonProps {
    carBusToEdit: ActivityCarBusState | null,
    getCalculatedActivity: (activity: ActivityCarBusState | null) => void,
    journeyDuration: DurationProps,
}

const CAR_BUS_PROPERTIES = {
    maxDistanceKm: 10000,
    minDistanceKm: 0,
    maxFuelConsumptionL: 10000,
    minFuelConsumptionL: 0,
    minDaysCount: 1,
    maxDaysCount: 1000,
    minPersonsCount: 1,
    maxPersonsCount: 1000,
    minTotalPassengerCount: 1,
    maxTotalPassengerCount: 1000,
}

// https://docs.mapbox.com/api/navigation/directions/#routing-profiles
const MAX_NUMBER_WAYPOINTS = 25
const MAX_MAPBOX_DISTANCE_FOR_ROUTING = 10000
const DEFAULT_ROUTE_STYLE = {
    'line-color': theme.colors.primary,
    'line-opacity': 1,
}
export const INITIAL_WAYPOINTS = [null, null]

const setMaxDaysCount = (day: number | null, journeyDuration: DurationProps) => {
    if (day === null) return CAR_BUS_PROPERTIES.maxDaysCount
    const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
    if (journeyDurationInDays === null || journeyDurationInDays === 0) return CAR_BUS_PROPERTIES.maxDaysCount
    return journeyDurationInDays - day + 1
}

const ChooseCarBus = ({
                          isEdit,
                          resetForm,
                          carBusToEdit,
                          getCalculatedActivity,
                          day,
                          journeyDuration,
                          maxPersons
                      }: ChooseCarBusProps) => {
    const [calculationImprovementsHint, setCalculationImprovementsHint] = useState<string | null>()
    const [waypointsFromCarBusToEditWasEdited, setWaypointsFromCarBusToEditWasEdited] = useState(false)
    const [hideCalculationResults, setHideCalculationResults] = useState(false)
    const {t, i18n} = useTranslation()
    const [vehicleTypeDropdownOptions, setVehicleTypeDropdownOptions] = useState<DropdownOption[]>([])
    const [disableIsHybridCheckbox, setDisableIsHybridCheckbox] = useState(false)
    const [fuelTypeDropdownOptions, setFuelTypeDropdownOptions] = useState<DropdownOption[]>([])
    const [carCategoryDropdownOptions, setCarCategoryDropdownOptions] = useState<DropdownOption[]>([])
    const [busCategoryDropdownOptions, setBusCategoryDropdownOptions] = useState<DropdownOption[]>([])
    const [disableCalculateButton, setDisableCalculateButton] = useState(false)
    const [disableTotalPassengerCount, setDisableTotalPassengerCount] = useState(true)
    const [calculatedDistanceOverwritten, setCalculatedDistanceOverwritten] = useState(false)
    const [formData, setFormData] = useState<FormProps>({
        vehicleType: null,
        fuelConsumption: null,
        manualEnteredDistanceKm: null,
        calculatedDistanceKm: null,
        fuelType: null,
        carCategory: null,
        busCategory: null,
        totalPassengerCount: null,
        acrissCode: null,
        countDays: 1,
        passengerCount: 1,
        waypoints: INITIAL_WAYPOINTS,
        route: null,
        isHybrid: false,
    })
    const [formErrorData, setFormErrorData] = useState<FormErrorProps>({
        calculatedDistanceError: false,
        manualEnteredDistanceError: false,
        fuelConsumptionError: false,
        personCountError: false,
        daysCountError: false,
        acrissCodeError: false,
        vehicleTypeError: false,
        totalPassengerCountError: false,
    })
    const [selectedLayer, setSelectedLayer] = useState(STREET_LAYER_NUMBER)
    const [pickAddressFromMapIndex, setPickAddressFromMapIndex] = useState<number | null>(null)
    const {resolvedLanguage} = i18n
    const mapContainerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<mapboxgl.Map | null>(null)
    const routeAnimationTimer = useRef<NodeJS.Timer | null>(null);
    const clickedCoordinates = useRef<LngLat | null>(null)
    const acrissCodeForValidation = useRef<AcrissCodeRequest | null>(null)
    const {
        vehicleType,
        fuelConsumption,
        manualEnteredDistanceKm,
        calculatedDistanceKm,
        fuelType,
        busCategory,
        totalPassengerCount,
        carCategory,
        acrissCode,
        countDays,
        passengerCount,
        waypoints,
        route,
        isHybrid
    } = formData
    const {
        calculatedDistanceError,
        manualEnteredDistanceError,
        fuelConsumptionError,
        personCountError,
        daysCountError,
        acrissCodeError,
        vehicleTypeError,
        totalPassengerCountError,
    } = formErrorData
    const previousWaypoints = usePrevious(waypoints)
    const waypointsRef = useRef<WaypointsProps | null>(null)
    const carBusForRequestFormatted = useRef<CarBusCalculationRequest[]>([])
    const waypointsWithoutEmptyWaypoints = waypoints.filter(Boolean)
    const maxDaysCount = useRef(setMaxDaysCount(day, journeyDuration))
    const maxPersonsCount = useRef(maxPersons !== null ? maxPersons : CAR_BUS_PROPERTIES.maxPersonsCount)
    const isFirstRender = useFirstRender()
    const [calculateEmissions, {
        data: resultCalculateEmissions,
        error: errorCalculateEmissions,
        isLoading: isCalculatingEmissions,
    }] = useCalculateEmissionsMutation()
    const [validateAcrissCode, {
        data: resultValidateAcrissCode,
        error: errorValidateAcrissCode,
    }] = useValidateAcrissCodeMutation()
    const [getCarPropsViaAcrissCode, {
        data: resultGetCarPropsViaAcrissCode,
        error: errorCarPropsViaAcrissCode,
    }] = useGetCarPropsViaAcrissCodeMutation()
    const [getRoute, {
        data: resultGetRouteFromCoordinates,
        error: errorRouteFromCoordinates,
        isLoading: loadingRouteFromCoordinates,
    }] = useGetRouteFromCoordinatesMutation()
    const [getAddressWithCoordinates, {
        data: resultGetAddressWithCoordinates,
        isLoading: isLoadingGetAddressWithCoordinates,
        error: errorGetAddressWithCoordinates
    }] = apiSlice.endpoints?.getAddressWithCoordinates.useLazyQuery()
    const {
        data: resultCarCalculatorProps,
        error: errorGetCarCalculatorProps
    } = useGetCarCalculatorPropsQuery()
    const [fetchComputedTotalPassengerCount, {
        data: resultComputedTotalPassengerCount,
        error: errorComputedTotalPassengerCount,
    }] = useLazyGetComputedTotalPassengerCountQuery()

    const _getCalculatedActivity = (hideResults: boolean, carBus: ActivityCarBusState | null) => {
        setHideCalculationResults(hideResults)
        getCalculatedActivity(carBus)
    }

    const _setWaypointsStateAndRelatedProps = (newWaypoints: WaypointsProps) => {
        waypointsRef.current = newWaypoints
        setWaypointsFromCarBusToEditWasEdited(true)
        if (newWaypoints.filter(Boolean).length === 1) {
            const newState = {
                "calculatedDistanceKm": null,
                "route": null,
                "waypoints": newWaypoints
            }
            if (manualEnteredDistanceKm === null || manualEnteredDistanceKm === 0) {
                _setFormData({
                    ...newState,
                    "manualEnteredDistanceKm": calculatedDistanceKm !== null ? calculatedDistanceKm : manualEnteredDistanceKm,
                })
            } else {
                _setFormData({...newState})
            }
            const routeLayer = mapRef.current?.getSource(ROUTE_SOURCE)
            if (routeLayer) {
                (routeLayer as mapboxgl.GeoJSONSource).setData({
                    type: 'LineString',
                    coordinates: []
                })
            }
        } else {
            _setFormData('waypoints', newWaypoints)
        }
    }

    const _setRawLineStringSource = (coordinates?: number[][]): GeoJSONSourceRaw => ({
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: coordinates ?? []
        },
        properties: {}
    })

    const _setNoAddress = (lng: number, lat: number) => `${t("SCREENS.CAR_BUS.NO_ADDRESS_REVERSE_GEOCODING")} (${lng}, ${lat})`

    const _getAddressViaMapClick = useCallback((event: MapMouseEvent) => {
        if (!isLoadingGetAddressWithCoordinates && pickAddressFromMapIndex !== null) {
            const {lngLat} = event
            const {lng, lat} = lngLat
            clickedCoordinates.current = lngLat
            if (resolvedLanguage) getAddressWithCoordinates({
                latitude: lat,
                longitude: lng,
                language: resolvedLanguage
            });
        }
    }, [isLoadingGetAddressWithCoordinates, pickAddressFromMapIndex, waypoints])

    const _setWaypointPopUp = useCallback((event: MapMouseEvent) => {
        event.originalEvent.stopPropagation()
        if (event.features) {
            const {features} = event
            const clickedFeature = features[0]
            const {properties} = clickedFeature
            //@ts-ignore
            const coordinates = clickedFeature.geometry.coordinates.slice()
            const address = properties?.full_address ?? ""
            //@ts-ignore
            const headline = properties.label
            while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            if (mapRef.current) {
                const popup = new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: false
                });
                const popupContainer = document.createElement("div");
                const rootElementForPopUpContainer = createRoot(popupContainer)
                if (waypointsRef.current) {
                    const indexOfWaypoint = waypointsRef.current.findIndex(waypoint => waypoint?.properties.full_address === address)
                    // Only god knows why I cannot simply use <IconButton /> component
                    // If I use it, I get t() (coming from react i18next) is undefined, which kind of makes sense because
                    // by creating a new root (createRoot(), which must be done to use React Components in the Mapbox pop up)
                    // it looses its context. The easiest solution would be to pass <I18nextProvider i18n={i18n}> to the newly created root
                    // but, this does not work (god knows why).
                    // The weird thing is: The <Text /> component also uses the t() function from i18next, and this works
                    // not sure why <IconButton /> fails
                    let TrashIcon: React.ReactNode = null
                    const departureOrArrivalWaypoint = indexOfWaypoint === 0 || indexOfWaypoint === waypointsRef.current.length - 1
                    if (
                        typeof indexOfWaypoint !== "undefined" &&
                        indexOfWaypoint > -1
                    ) {
                        TrashIcon = <Tooltip
                            placement={"top"}
                            title={t(`TOOLTIP.CAR_BUS.${departureOrArrivalWaypoint ? 'REMOVE_ADDRESS' : 'DELETE_WAYPOINT'}`)}>
                            <div>
                                <Button
                                    onClick={() => {
                                        _deleteWaypoint(indexOfWaypoint)
                                        popup.remove()
                                    }}
                                    containerClasses={clsx('icon-button-without-button', 'icon-button-small')}
                                    onlyIcon={true}
                                    size={"small"}>
                                    <Icon
                                        color={theme.colors.primary}
                                        size={"small"}
                                        icon={departureOrArrivalWaypoint ? "deleteLeft" : "minus"}/>
                                </Button>
                            </div>
                        </Tooltip>
                    }

                    rootElementForPopUpContainer.render(
                        <div>
                            <div className={"choose-car-bus-route-pop-up-headliner-wrapper"}>
                                <Text
                                    variant={"headlineSmall"}>{headline}</Text>
                                {TrashIcon}
                            </div>
                            <Text>{address}</Text>
                        </div>
                    )

                    popup
                        .setLngLat(coordinates)
                        .setDOMContent(popupContainer)
                        .addTo(mapRef.current);
                }
            }
        }
    }, [])

    const _setWaypointsData = (waypoints: WaypointsProps) => {
        const waypointsLayer = mapRef.current?.getSource(WAYPOINTS_SOURCE)
        if (waypointsLayer) {
            (waypointsLayer as mapboxgl.GeoJSONSource).setData(_convertWaypointsForMapRendering(waypoints))
        }
    }

    const _addRouteLayer = (mapRef: React.MutableRefObject<mapboxgl.Map | null>) => {
        _addEmptyRouteSource()
        mapRef.current?.addLayer({
            id: ROUTE_LAYER,
            type: 'line',
            source: ROUTE_SOURCE,
            paint: {
                ...DEFAULT_ROUTE_STYLE,
                'line-width': 5
            }
        })
        if (isEdit && carBusToEdit && carBusToEdit.route !== null) _animateRoute(carBusToEdit.route.coordinates)

    }

    const _addEmptyRouteSource = () => mapRef.current?.addSource(ROUTE_SOURCE, {
        type: "geojson",
        data: _setRawLineStringSource()
    });

    const _fitMapToCoordinates = (coordinates: number[][] | Position[]) => {
        if (!Array.isArray(coordinates) || coordinates.length === 0) return
        const firstCoord = coordinates.find(coord => Array.isArray(coord) && coord.length === 2) as [number, number] | undefined;
        if (!firstCoord) {
            console.error("Invalid coordinates: Unable to fit map.")
            return
        }
        const bounds = new mapboxgl.LngLatBounds(firstCoord, firstCoord)
        coordinates.forEach((coordinate) => {
            if (Array.isArray(coordinate) && coordinate.length === 2) {
                bounds.extend(coordinate as [number, number])
            } else {
                console.warn("Invalid coordinate skipped:", coordinate)
            }
        })
        const overlayContainer = document.getElementsByClassName("choose-car-bus-route-waypoints-selection-container")
        if (overlayContainer.length > 0) {
            const width = overlayContainer[0].clientWidth
            const padding = 50
            mapRef?.current?.fitBounds(bounds, {
                padding: {
                    left: width + 2 * padding,
                    right: padding,
                    top: padding,
                    bottom: padding
                }
            });
        }
    };

    const _stopRouteAnimation = () => {
        if (routeAnimationTimer.current) {
            window.clearInterval(routeAnimationTimer.current)
            mapRef?.current?.setPitch(0)
            if (isEdit && carBusToEdit) {
                const {route} = carBusToEdit
                if (route !== null) {
                    const {coordinates} = route
                    _fitMapToCoordinates(coordinates)
                    const routeLayer = mapRef.current?.getSource(ROUTE_SOURCE)
                    if (routeLayer) {
                        (routeLayer as mapboxgl.GeoJSONSource).setData(_setRawLineStringSource(coordinates))
                    }
                }
            }
            routeAnimationTimer.current = null
        }
    }

    const _returnIsHybridValueOnAcrissCode = (acrissCodeLastCharacter: string) => {
        switch (acrissCodeLastCharacter) {
            case "H":
                return true
            case "I":
                return true
            default:
                return false
        }
    }

    const isInvalidFuelConsumption = useMemo(() => {
        return Number.isNaN(fuelConsumption) || fuelConsumption === null || fuelConsumption === 0;
    }, [fuelConsumption]);
    
    useEffect(() => {
        setDisableTotalPassengerCount(isInvalidFuelConsumption);
        if (isInvalidFuelConsumption && !busCategory) {
            _setFormData("totalPassengerCount", null);
        }
        if (!isInvalidFuelConsumption && busCategory) {
            fetchComputedTotalPassengerCount({busCategory});
        }
    }, [busCategory,isInvalidFuelConsumption, fetchComputedTotalPassengerCount]);

    useEffect(() => {
        if (resultComputedTotalPassengerCount?.computedTotalPassengerCount != null) {
            _setFormErrorData("totalPassengerCountError", false)
            _renderToast("TOASTS.SUCCESS.COMPUTED_TOTAL_PASSENGER_COUNT", "success")
            _setFormData(
                "totalPassengerCount",
                resultComputedTotalPassengerCount.computedTotalPassengerCount
            );
        } else {
            if(resultComputedTotalPassengerCount != null ){
                _renderToast("TOASTS.ERROR.COMPUTED_TOTAL_PASSENGER_COUNT", "error")
                _setFormData("totalPassengerCount", null);        
                _setFormErrorData("totalPassengerCountError", true)
            }
        }
    }, [resultComputedTotalPassengerCount]);

    useEffect(() => {
        if (formData.totalPassengerCount != null) {
            _setFormErrorData("totalPassengerCountError", false)
        }
    }, [formData.totalPassengerCount]);


    useEffect(() => {
        if (!mapContainerRef.current) return;
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: MAPBOX_STREET_STYLE,
            center: [10.451526, 51.165691],
            zoom: 4,
            maxZoom: 15,
            pitch: 0,
            logoPosition: "bottom-right",
        });

        mapRef.current.on("load", () => {
            mapRef.current?.addControl(new mapboxgl.NavigationControl(), "top-right")
            _addRouteLayer(mapRef)
            _addWaypointsLayer(mapRef)
            mapRef.current?.on('click', WAYPOINTS_LAYER, _setWaypointPopUp);
            mapRef.current?.on("click", _getAddressViaMapClick)
            mapRef.current?.on("click", _stopRouteAnimation)
            mapRef.current?.on('styledata', () => {
                let waypointsLayer = mapRef.current?.getSource(WAYPOINTS_SOURCE)
                if (!waypointsLayer) {
                    _addWaypointsLayer(mapRef)
                    waypointsLayer = mapRef.current?.getSource(WAYPOINTS_SOURCE)
                }
                waypointsRef.current && (waypointsLayer as mapboxgl.GeoJSONSource)
                    .setData(
                        _setDataForRendering(
                            _setWaypointsProperties(waypointsRef.current)
                        )
                    )

            })
        })

        return () => {
            mapRef.current?.off("click", _getAddressViaMapClick);
            mapRef.current?.off("click", _setWaypointPopUp);
            mapRef.current?.remove();
        };
    }, [])

    useEffect(() => {
        if (disableCalculateButton) setDisableCalculateButton(false)
        if (resultCalculateEmissions) _getCalculatedActivity(true, null)
    }, [
        vehicleType,
        fuelConsumption,
        manualEnteredDistanceKm,
        calculatedDistanceKm,
        fuelType,
        busCategory,
        totalPassengerCount,
        carCategory,
        acrissCode,
        countDays,
        passengerCount,
        waypoints,
        isHybrid
    ])

    // only set the formDate with carBusToEdit on first render, otherwise, if user edits data and recalculates,
    // the newly entered data gets replaced with original carBusToEdit
    useEffect(() => {
        if (isEdit && carBusToEdit && isFirstRender) {
            const updatedCarBusToEdit = cloneDeep(carBusToEdit)
            if (carBusToEdit.waypoints.length === 0) updatedCarBusToEdit.waypoints = INITIAL_WAYPOINTS
            setFormData(updatedCarBusToEdit)
        }
    }, [isEdit, carBusToEdit]);

    useEffect(() => {
        if (errorCarPropsViaAcrissCode) _renderToast("TOASTS.ERROR.ROUTE", "error")
    }, [errorCarPropsViaAcrissCode]);

    useEffect(() => {
        if (errorCarPropsViaAcrissCode) _renderToast("TOASTS.ERROR.CAR_PROPS_ACRISS_CODE", "error")
    }, [errorCarPropsViaAcrissCode]);

    useEffect(() => {
        if (errorGetCarCalculatorProps) _renderToast("TOASTS.ERROR.GENERAL", "error")
    }, [errorGetCarCalculatorProps])

    useEffect(() => {
        if (errorValidateAcrissCode) {
            _renderToast("TOASTS.ERROR.VALIDATE_ACRISS_CODE", "error")
            acrissCodeForValidation.current = null
        }
    }, [errorValidateAcrissCode]);

    useEffect(() => {
        if (isGeneralCalculatedCarBusEmissionsResponse(errorCalculateEmissions)) {
            let informativeToastShowed = false
            setCalculationImprovementsHint(null)
            const {data: {carActivities: carBusActivities}} = errorCalculateEmissions
            const errorStrings: string[] = []
            carBusActivities?.forEach(carBus => {
                if (carBus.errorCodes) carBus.errorCodes.forEach(error => {
                    const {code} = error
                    switch (code) {
                        case 751:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 752:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 753:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 754:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 755:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 757:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 758:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 759:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 760:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 761:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 762:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        default:
                            errorStrings.push(error.message)
                    }
                })
            })

            if (errorStrings.length > 0) {
                _renderToast(errorStrings.join("\n \n"), "error")
                informativeToastShowed = true
            }
            if (!informativeToastShowed) toast(<ToastWithTextOnly
                    message={"TOASTS.ERRORS.GENERAL"}/>,
                {progress: undefined, type: "error"}
            )

            const emissions = carBusActivities ?? null
            const carBus: ActivityCarBusState = {
                common: {
                    ..._setCommonActivityProps(),
                    totalEmissions: emissions,
                    originalTotalEmissions: cloneDeep(emissions),
                },
                ..._setCarBusProps()
            }
            _getCalculatedActivity(false, carBus)
        }
    }, [errorCalculateEmissions])

    useEffect(() => {
        if (errorGetAddressWithCoordinates) setPickAddressFromMapIndex(null)
    }, [errorGetAddressWithCoordinates])

    useEffect(() => {
        if (resultValidateAcrissCode) {
            if (resultValidateAcrissCode.isValid && acrissCodeForValidation.current !== null) {
                const {lastCharacter} = acrissCodeForValidation.current
                setDisableIsHybridCheckbox(_returnIsHybridValueOnAcrissCode(lastCharacter))
                getCarPropsViaAcrissCode(acrissCodeForValidation.current)
                _renderToast("TOASTS.SUCCESS.CAR_DATA_VIA_ACRISS_CODE", "success")
                _setFormErrorData("acrissCodeError", false)
                acrissCodeForValidation.current = null

            } else {
                if (!resultValidateAcrissCode.isValid) {
                    _renderToast("TOASTS.ERROR.WRONG_ACRISS_CODE", "error")
                    acrissCodeForValidation.current = null
                    setDisableIsHybridCheckbox(false)
                    _setFormErrorData("acrissCodeError", true)
                }

            }
        }
    }, [resultValidateAcrissCode]);

    useEffect(() => {
        if (resultCarCalculatorProps) {
            const {
                fuelTypes,
                klimaLinkBusCategories,
                klimaLinkCarCategories,
                vehicleTypes,
            } = resultCarCalculatorProps
            setVehicleTypeDropdownOptions(convertCarBusEnumToDropdownFormat(vehicleTypes, i18n, WHICH_CAR_BUS_PROPS.GENERAL))
            setFuelTypeDropdownOptions(convertCarBusEnumToDropdownFormat(fuelTypes, i18n, WHICH_CAR_BUS_PROPS.FUEL_TYPE))
            setCarCategoryDropdownOptions(convertCarBusEnumToDropdownFormat(klimaLinkCarCategories, i18n, WHICH_CAR_BUS_PROPS.CAR))
            setBusCategoryDropdownOptions(convertCarBusEnumToDropdownFormat(klimaLinkBusCategories, i18n, WHICH_CAR_BUS_PROPS.BUS))
        }
    }, [resultCarCalculatorProps, resolvedLanguage]);

    useEffect(() => {

        if (!resultCarCalculatorProps?.fuelTypes) return;
        const allOptions = convertCarBusEnumToDropdownFormat(
            resultCarCalculatorProps?.fuelTypes,
            i18n,
            WHICH_CAR_BUS_PROPS.FUEL_TYPE
        );
        const filteredOptions = allOptions.filter(opt => formData.vehicleType !== 'BUS' || opt.raw.name !== 'PETROL');

        setFuelTypeDropdownOptions(filteredOptions);
    }, [resultCarCalculatorProps?.fuelTypes, formData.vehicleType, i18n]);

    useEffect(() => {
        if (resultGetCarPropsViaAcrissCode) {
            const {carCategory, fuelType} = resultGetCarPropsViaAcrissCode
            let isHybridCar = isHybrid
            if (acrissCode !== null) isHybridCar = _returnIsHybridValueOnAcrissCode(acrissCode[acrissCode.length - 1])
            _setFormData({
                fuelType,
                carCategory,
                isHybrid: isHybridCar,
            })
        }
    }, [resultGetCarPropsViaAcrissCode])

    useEffect(() => {
        if (resultCalculateEmissions) {
            setDisableCalculateButton(true)
            const {carActivities, emissionKgCO2eTotal} = resultCalculateEmissions
            if (typeof carActivities !== 'undefined') {
                const calculatedCarBus = carActivities[0]
                if (isCalculatedCarBusEmissionsResponse(calculatedCarBus)) {
                    if (emissionKgCO2eTotal !== null && carBusForRequestFormatted.current) {
                        let acrissCode: string | null = null
                        const {
                            carInfo,
                            distance,
                            fuelConsumption,
                            vehicleType,
                            busCategory,
                        } = carBusForRequestFormatted.current[0]

                        if (carInfo && carInfo.acrissCode !== null) acrissCode = `${carInfo.acrissCode.firstCharacter}${carInfo.acrissCode.lastCharacter}`
                        const {tierMessage, improvements} = generateCarBusCalculationTierHintString({
                            acrissCode,
                            manualEnteredDistanceKm: distance,
                            fuelConsumption,
                            vehicleType,
                            carCategory: carInfo?.carCategory ?? null,
                            busCategory,
                            countDays
                        } as ActivityCarBusState, i18n)
                        setCalculationImprovementsHint(improvements)
                        const calculationTierHint = [t("GENERAL.EMISSIONS_CALCULATED"), tierMessage].join("\n")
                        _renderToast(calculationTierHint, "success")
                    }
                    const carBus: ActivityCarBusState = {
                        common: {
                            ..._setCommonActivityProps(),
                            totalEmissions: carActivities,
                            originalTotalEmissions: cloneDeep(carActivities),
                        },
                        ..._setCarBusProps()
                    }
                    _getCalculatedActivity(false, carBus)
                }
            }
        }
    }, [resultCalculateEmissions])

    useEffect(() => {
        if (resultGetAddressWithCoordinates) {
            let feature = {} as GeocodingFeature
            if (resultGetAddressWithCoordinates.features.length === 0 && clickedCoordinates.current) {
                // This is a bit of a workaround. If the user clicks somewhere in the map where the reverse geocoding from
                // mapbox does not return an address, we want to display the coordinates. However, simply passing the coordinates
                // to the <AutocompleteQuery /> component would require significant changes in the <AutocompleteQuery /> component
                // because of that, the full_address of the GeocodingFeature is set to the coordinates, which then are displayed in
                // the <TextInput />. Properties added here must match the properties checked in isGeocodingFeature() and must fulfill
                // the minimum requirements to be a valid GeoJson
                const {lng, lat} = clickedCoordinates.current
                feature.id = uuidv4()
                feature.type = "Feature"
                feature.properties = {} as GeocodingFeatureProperties
                feature.properties.full_address = _setNoAddress(lng, lat)
                feature.geometry = {} as GeoJSON.Point
                feature.geometry.type = "Point"
                feature.geometry.coordinates = [lng, lat]
            } else {
                feature = resultGetAddressWithCoordinates.features[0]
            }
            if (pickAddressFromMapIndex !== null) {
                const updatedWaypoints = cloneDeep(waypoints)
                updatedWaypoints[pickAddressFromMapIndex] = feature
                _setWaypointsStateAndRelatedProps(updatedWaypoints)
                setPickAddressFromMapIndex(null)
            } else {
                const indexOfWaypointWithoutAddress = waypoints.indexOf(null)
                if (indexOfWaypointWithoutAddress >= 0 && feature) {
                    const updatedWaypoints = cloneDeep(waypoints)
                    if (updatedWaypoints[indexOfWaypointWithoutAddress] === null) {
                        updatedWaypoints[indexOfWaypointWithoutAddress] = feature
                        _setWaypointsStateAndRelatedProps(updatedWaypoints)
                    }
                }
            }
        }
    }, [resultGetAddressWithCoordinates])

    useEffect(() => {
        if (resultGetRouteFromCoordinates) {
            if (resultGetRouteFromCoordinates.code === "NoRoute") {
                _renderToast(t("TOASTS.WARNING.NO_ROUTE_FOUND"), "warning")
                _fitMapToWaypointsCoordinates()
            } else {
                if (resultGetRouteFromCoordinates.routes[0].geometry.type === "LineString") {
                    const selectedRoute = resultGetRouteFromCoordinates.routes[0]
                    const {geometry: routeToDisplay} = selectedRoute
                    const routeLayer = mapRef.current?.getSource(ROUTE_SOURCE)
                    if (routeLayer) {
                        (routeLayer as mapboxgl.GeoJSONSource).setData(routeToDisplay)
                    }
                    const distance = roundKm(selectedRoute.distance / 1000)
                    _setFormData({
                        "manualEnteredDistanceKm": distance,
                        "calculatedDistanceKm": distance,
                        'route': routeToDisplay
                    })
                    mapRef.current?.setPaintProperty(ROUTE_LAYER, 'line-opacity', 1);
                    const {coordinates} = routeToDisplay
                    _fitMapToCoordinates(coordinates)
                } else {
                    _renderToast(t("TOASTS.WARNING.NO_ROUTE_FOUND"), "warning")
                    _fitMapToWaypointsCoordinates()
                }
            }
        }
    }, [resultGetRouteFromCoordinates])

    useEffect(() => {
        if (errorRouteFromCoordinates) {
            mapRef.current?.setPaintProperty(ROUTE_LAYER, 'line-opacity', DEFAULT_ROUTE_STYLE["line-opacity"])
            mapRef.current?.setPaintProperty(ROUTE_LAYER, 'line-color', DEFAULT_ROUTE_STYLE["line-color"])
        }
    }, [errorRouteFromCoordinates])

    useEffect(() => {
        if (loadingRouteFromCoordinates) {
            mapRef.current?.setPaintProperty(ROUTE_LAYER, 'line-opacity', 0.3)
            mapRef.current?.setPaintProperty(ROUTE_LAYER, 'line-color', theme.colors.primaryLight)
        }
    }, [loadingRouteFromCoordinates]);

    useOutsideClick(mapContainerRef, () => {
        setPickAddressFromMapIndex(null)
    }, [mapContainerRef])

    /**
     * The condition in _getAddressViaMapClick()
     * cannot be checked, since the values for waypoints, pickAddressFromMapIndex, isLoadingGetAddressWithCoordinates are
     * "written" into _getAddressViaMapClick() upon creation of the event handler. Because of that, we must bind and unbind
     * the event handler, in order to always have the most up to date values for
     * waypoints, pickAddressFromMapIndex, isLoadingGetAddressWithCoordinates in there.
     */
    useEffect(() => {
        mapRef.current?.off("click", _getAddressViaMapClick);
        mapRef.current?.on("click", _getAddressViaMapClick);

        return () => {
            mapRef.current?.off("click", _getAddressViaMapClick);
        };
    }, [waypoints, pickAddressFromMapIndex, isLoadingGetAddressWithCoordinates])

    useEffect(() => {
        if (waypointsFromCarBusToEditWasEdited) {
            _setWaypointsData(waypoints)
            const previousWaypointsWithoutEmptyWaypoints = previousWaypoints?.filter(Boolean)
            if (waypointsWithoutEmptyWaypoints.length >= 2 && !isEqual(previousWaypointsWithoutEmptyWaypoints, waypointsWithoutEmptyWaypoints)) {
                const validCoordinates = waypoints
                    .filter(waypoint => waypoint && waypoint.geometry.coordinates.length >= 2)
                    .map(waypoint => ({
                        latitude: waypoint!!.geometry.coordinates[1],
                        longitude: waypoint!!.geometry.coordinates[0],
                    }))
                const totalDistanceKm = validCoordinates.reduce((acc, curr, index, arr) => {
                    if (index === 0) return acc
                    const distance = haversine(arr[index - 1], curr, {radius: haversine.EARTH.KM})
                    return acc + distance;
                }, 0)
                if (totalDistanceKm <= MAX_MAPBOX_DISTANCE_FOR_ROUTING) {
                    getRoute(validCoordinates)
                } else {
                    _renderToast(t("TOASTS.WARNING.ROUTE_TOO_LONG", {maxDistance: formatDistanceKmString(MAX_MAPBOX_DISTANCE_FOR_ROUTING, resolvedLanguage)}), "warning")
                    _fitMapToWaypointsCoordinates()
                }
            }
        }
    }, [waypoints])

    const _addWaypointsLayer = (mapRef: React.MutableRefObject<mapboxgl.Map | null>) => {
        _addEmptyWaypointsSource()
        mapRef.current?.addLayer({
            id: WAYPOINTS_LAYER,
            type: 'circle',
            source: WAYPOINTS_SOURCE,
            paint: {
                'circle-stroke-width': 1,
                'circle-stroke-color': '#ffffff',
                'circle-color': [
                    'case',
                    ['==', ['get', 'color'], 'primary'], theme.colors.primary,
                    ['==', ['get', 'color'], 'secondary'], theme.colors.secondary,
                    ['==', ['get', 'color'], 'black'], theme.colors.black,
                    theme.colors.black
                ],
                'circle-radius': [
                    'case',
                    ['==', ['get', 'size'], 'small'], 3,
                    ['==', ['get', 'size'], 'medium'], 6,
                    ['==', ['get', 'size'], 'large'], 9,
                    6
                ]
            }
        })
        mapRef.current?.addLayer({
            id: WAYPOINTS_COUNT_LABEL,
            type: "symbol",
            source: WAYPOINTS_SOURCE,
            layout: {
                "text-field": ["get", "label"],
                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                "text-size": 12,
                "text-anchor": "bottom",
                "text-offset": [0, -0.8]
            }
        })
        if (isEdit && carBusToEdit) {
            const {waypoints} = carBusToEdit
            if (waypoints !== null) {
                const {route} = carBusToEdit
                const waypointLayer = mapRef.current?.getSource(WAYPOINTS_SOURCE)
                if (waypointLayer) {
                    (waypointLayer as mapboxgl.GeoJSONSource).setData(_convertWaypointsForMapRendering(waypoints))
                }

                if (route === null) _fitMapToWaypointsCoordinates()
            }
        }
    }

    const _fitMapToWaypointsCoordinates = () => {
        const waypointsCoordinates: number[][] = []
        waypoints.forEach(waypoint => waypoint && waypointsCoordinates.push(waypoint.geometry.coordinates))
        _fitMapToCoordinates(waypointsCoordinates)
    }

    const _setWaypointsProperties = (waypoints: WaypointsProps) => {
        const clonedWaypoints = cloneDeep(waypoints)
        clonedWaypoints.forEach((clonedWaypoint, index) => {
            if (clonedWaypoint) {
                const properties = clonedWaypoint.properties as StyledGeocodingFeatureProperties;
                if (index === 0 || index === waypoints.length - 1) {
                    properties["color"] = "primary"
                    properties["size"] = "large"
                    if (index === 0) properties["label"] = t("GENERAL.START")
                    if (index === waypoints.length - 1) properties["label"] = t("GENERAL.DESTINATION")
                } else {
                    properties["color"] = "secondary"
                    properties["size"] = "medium"
                    properties["label"] = `${index}. ${t("GENERAL.STOPOVER")}`
                }
            }
        })
        return clonedWaypoints
    }

    const _addEmptyWaypointsSource = () => mapRef.current?.addSource(WAYPOINTS_SOURCE, {
        type: "geojson",
        data: _setFeatureCollectionSource()
    });

    const _setFeatureCollectionSource = (features?: GeoJSONSourceOptions["data"]): GeoJSONSourceRaw => ({
            type: "FeatureCollection",
            features: features ?? [],
        }
    )

    const _convertWaypointsForMapRendering = (waypoints: WaypointsProps) => _setDataForRendering(_setWaypointsProperties(waypoints))

    const _setDataForRendering = (waypoints: WaypointsProps) => _setFeatureCollectionSource(waypoints.filter(waypoint => waypoint !== null))

    // https://docs.mapbox.com/mapbox-gl-js/example/live-update-feature/
    const _animateRoute = (coordinates: Position[]) => {
        //@ts-ignore
        mapRef.current?.jumpTo({center: coordinates[0], zoom: 10});
        mapRef.current?.setPitch(30);
        let i = 0;
        const coordinatesForAnimation: Position[] = []
        const routeLayer = mapRef.current?.getSource(ROUTE_SOURCE)
        routeAnimationTimer.current = setInterval(() => {
            if (i < coordinates.length) {
                coordinatesForAnimation.push(coordinates[i])
                //@ts-ignore
                mapRef.current?.panTo(coordinates[i], {
                    duration: 1
                });
                if (routeLayer) {
                    (routeLayer as mapboxgl.GeoJSONSource).setData(_setRawLineStringSource(coordinatesForAnimation))
                }
                i++;
            } else {
                if (routeAnimationTimer.current) window.clearInterval(routeAnimationTimer.current)
            }
        }, 1);
    }

    const _setCarBusProps = (): Omit<ActivityCarBusState, "common"> => ({
        vehicleType,
        fuelConsumption,
        manualEnteredDistanceKm,
        calculatedDistanceKm,
        fuelType,
        busCategory,
        totalPassengerCount,
        carCategory,
        acrissCode,
        countDays,
        passengerCount,
        isHybrid,
        waypoints: waypointsWithoutEmptyWaypoints,
        originalWaypoints: cloneDeep(waypointsWithoutEmptyWaypoints),
        originalRoute: route,
        originalCountDays: countDays,
        route,
        informationFromLoadedBundle: carBusToEdit ? carBusToEdit.informationFromLoadedBundle : null,
        backendId: carBusToEdit?.backendId ?? null
    })

    const _setCommonActivityProps = (): ActivityCommonProperties["common"] => {
        const id = carBusToEdit?.common.id ?? uuidv4()
        return {
            ...setCommonCarBusActivityProps(),
            day,
            editId: null,
            id,
            hideLoadBundleInformation: carBusToEdit ? true : false,
            groupId: carBusToEdit?.common.groupId ?? null,
            semanticId: id,
            originalRequest: cloneDeep(carBusForRequestFormatted.current),
            request: carBusForRequestFormatted.current,
        }
    }

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={t(message)}/>,
        {progress: undefined, type}
    )

    const _setWaypoints = (newWayPoint: GeocodingFeature | null, index: number) => {
        _stopRouteAnimation()
        const updatedWayPoints = cloneDeep(waypoints)
        updatedWayPoints[index] = newWayPoint
        _setWaypointsStateAndRelatedProps(updatedWayPoints)
        if (newWayPoint !== null && updatedWayPoints.filter(Boolean).length === 1) {
            const {geometry: {coordinates}} = newWayPoint
            mapRef.current?.flyTo({
                center: [coordinates[0], coordinates[1]],
                zoom: 10,
                pitch: 0,
                bearing: 0,
                duration: 2500,
                essential: false
            })
        }
    }

    const _setLabel = (index: number) => {
        if (index === 0) return "LABELS.CAR_BUS.FIRST_WAYPOINT"
        if (index === waypoints.length - 1) return "LABELS.CAR_BUS.LAST_WAYPOINT"
        return `${index}. ${t("LABELS.CAR_BUS.WAYPOINT")}`
    }

    const _deleteWaypoint = (index: number) => {
        const updatedWayPoints = cloneDeep(waypointsRef.current)
        if (updatedWayPoints) {
            const departureOrArrivalWaypoint = index === 0 || index === updatedWayPoints.length - 1
            if (departureOrArrivalWaypoint) {
                updatedWayPoints[index] = null
            } else {
                updatedWayPoints.splice(index, 1)
            }
            _setWaypointsStateAndRelatedProps(updatedWayPoints)
        }
    }

    const _renderAutocompleteQuery = (index: number, waypoint: GeocodingFeature | null) => {
        const disableIconButton = pickAddressFromMapIndex === index ? false : (isLoadingGetAddressWithCoordinates || pickAddressFromMapIndex !== null)
        const showDeleteWaypointIcon = index === 0 || index === waypoints.length - 1
        const DeleteWaypointIconButton = <IconButton
            tooltip={"TOOLTIP.CAR_BUS.DELETE_WAYPOINT"}
            containerClasses={"choose-car-bus-route-icon-offset"}
            icon={"minus"}
            onClick={() => _deleteWaypoint(index)}
            disabled={disableIconButton}/>
        return <div
            key={index}
            className={"choose-car-bus-route-autocomplete-query-wrapper"}>
            <AutocompleteQuery
                disabled={pickAddressFromMapIndex !== null || loadingRouteFromCoordinates}
                containerStyle={{width: '100%'}}
                placeholder={"GENERAL.ADDRESS"}
                setSelection={selection => _setWaypoints(selection as GeocodingFeature | null, index)}
                label={_setLabel(index)}
                defaultValue={waypoint}
                required={false}
                queryType={AUTOCOMPLETE_QUERY_TYPE.freelyTypedAddress}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.car_bus}/>
            {index === pickAddressFromMapIndex && isLoadingGetAddressWithCoordinates ?
                <>
                    {!showDeleteWaypointIcon && DeleteWaypointIconButton}
                    <Spinner
                        classes={"choose-car-bus-route-icon-offset"}
                        size={SMALL_SPINNER_SIZE}
                        fullPage={false}/></> :
                <>
                    {!showDeleteWaypointIcon && DeleteWaypointIconButton}
                    <IconButton
                        disabled={disableIconButton}
                        tooltip={"TOOLTIP.CAR_BUS.SELECT_STOP_FROM_MAP"}
                        containerClasses={"choose-car-bus-route-icon-offset"}
                        size={"small"}
                        icon={"locationPin"}
                        onClick={() => {
                            _stopRouteAnimation()
                            if (pickAddressFromMapIndex === null) {
                                setPickAddressFromMapIndex(index)
                            } else {
                                setPickAddressFromMapIndex(null)
                            }
                        }}/>
                </>}
        </div>
    }

    const _renderTextInputWaypoints = () => {
        const maxNumberOfWaypointsReached = waypoints.length === MAX_NUMBER_WAYPOINTS
        const allWaypoints = waypoints.map((waypoint, index) => {
            if (index === 0 || index === waypoints.length - 1) return _renderAutocompleteQuery(index, waypoint)
            return <Fieldset
                key={index}
                role={"group"}
                fieldsetClasses={'calculate-activity-stops-wrapper'}
                showLegend={index === 1}
                required={false}
                legend={waypoints.length > 3 ? "GENERAL.STOPOVERS" : "GENERAL.STOPOVER"}>
                {_renderAutocompleteQuery(index, waypoint)}
            </Fieldset>
        })

        allWaypoints.splice(
            allWaypoints.length - 1,
            0,
            <IconAndText
                disabled={isLoadingGetAddressWithCoordinates || maxNumberOfWaypointsReached || loadingRouteFromCoordinates}
                key={waypoints.length}
                tooltip={maxNumberOfWaypointsReached ? t("TOOLTIP.CAR_BUS.MAX_NUMBER_WAYPOINTS", {maxNumber: MAX_NUMBER_WAYPOINTS}) : undefined}
                text={"SCREENS.CAR_BUS.ADD_STOPOVER"}
                onClick={() => _setWaypointsStateAndRelatedProps([...waypoints.slice(0, -1), null, ...waypoints.slice(-1)])}
                iconColor={theme.colors.primary}
                icon={'plus'}/>
        )
        return <div className={"choose-car-bus-route-waypoints-selection-container"}>
            <SpinnerOverlay show={loadingRouteFromCoordinates} fullScreen={false}/>
            {allWaypoints}
        </div>
    }

    const _setLayerSelection = (selection: number) => {
        _stopRouteAnimation()
        setSelectedLayer(selection)
        if (selection === STREET_LAYER_NUMBER) {
            mapRef.current?.setStyle(MAPBOX_STREET_STYLE)
            mapRef.current?.setPitch(0)
        }
        if (selection === SATELLITE_LAYER_NUMBER) {
            mapRef.current?.setStyle("mapbox://styles/mapbox/satellite-streets-v12")
            mapRef.current?.addSource(MAPBOX_DEM_SOURCE, {
                type: 'raster-dem',
                url: 'mapbox://mapbox.terrain-rgb'
            })
            mapRef.current?.setTerrain({
                source: MAPBOX_DEM_SOURCE,
                exaggeration: 2
            })
            mapRef.current?.setFog({
                'color': 'rgb(220, 159, 159)',
                'high-color': 'rgb(36, 92, 223)',
                'horizon-blend': 0.4
            })
            mapRef.current?.setPitch(30)
        }
        if (selection === OUTDOOR_LAYER_NUMBER) {
            mapRef.current?.setStyle("mapbox://styles/mapbox/outdoors-v12")
            mapRef.current?.setPitch(0)
        }
    }

    const _setFormData = <K extends keyof FormProps>(
        keyOrUpdates: K | Partial<FormProps>,
        payload?: FormProps[K]
    ) => {
        setFormData({
            ...formData,
            ...(typeof keyOrUpdates === "string" ? {[keyOrUpdates]: payload} : keyOrUpdates)
        })
    }

    const _getDefaultOption = <T extends keyof FormProps>(
        dropdownOptions: DropdownOption[],
        selectedValue: FormProps[T]
    ): number | null => dropdownOptions.filter(option => option.raw.name === selectedValue)[0]?.value

    const _renderCalculatedDistance = () => {
        const Distance = <div
            className={clsx(
                "choose-car-bus-route-distance-from-map",
                "choose-car-bus-route-map-overlay",
                calculatedDistanceOverwritten && "choose-car-bus-route-calculated-distance-disabled",
                calculatedDistanceError && "choose-car-bus-route-calculated-distance-error"
            )}>
            <Text
                weight={calculatedDistanceError ? "bold" : "regular"}
                color={(calculatedDistanceError || calculatedDistanceOverwritten) ? "white" : "black"}
                variant="subtitleLarge">
                {`${t("GENERAL.DISTANCE")}: ${calculatedDistanceKm === null ? NO_DATA_STRING : calculatedDistanceKm} km`}
            </Text>
        </div>

        if (calculatedDistanceError) return <Tooltip
            placement={"top"}
            title={t("ERRORS.CAR_BUS.NOT_DISTANCE_FROM_ROUTE")}>{Distance}</Tooltip>

        if (calculatedDistanceOverwritten) return <Tooltip title={t("TOOLTIP.CAR_BUS.CALCULATED_DISTANCE_OVERWRITTEN")}>
            {Distance}
        </Tooltip>

        return Distance
    }

    const _evaluateForm = () => {
        const {
            maxDistanceKm,
            minDistanceKm,
            maxFuelConsumptionL,
            minFuelConsumptionL,
            minDaysCount,
            minPersonsCount,
            maxTotalPassengerCount,
            minTotalPassengerCount,
        } = CAR_BUS_PROPERTIES
        let manualEnteredDistanceError = false
        let calculatedDistanceError = false
        let vehicleTypeError = vehicleType === null
        let fuelConsumptionError = false
        let daysCountError = false
        let missingInformation = false
        let totalPassengerCountError = false
        const personCountError = passengerCount > maxPersonsCount.current || passengerCount < minPersonsCount
        if (manualEnteredDistanceKm !== null) {
            if (manualEnteredDistanceKm > maxDistanceKm || manualEnteredDistanceKm < minDistanceKm) manualEnteredDistanceError = true
        } else {
            if (calculatedDistanceKm !== null && (calculatedDistanceKm > maxDistanceKm || calculatedDistanceKm < minDistanceKm)) calculatedDistanceError = true
        }

        if (vehicleType === VEHICLE_TYPE.car) {
            if (carCategory === null && acrissCode === null) {
                daysCountError = countDays > maxDaysCount.current || countDays < minDaysCount
                fuelConsumptionError = fuelConsumption !== null && (fuelConsumption > maxFuelConsumptionL || fuelConsumption < minFuelConsumptionL)
                if (fuelConsumption === null || fuelConsumption === 0) {
                    missingInformation = true
                    toast(<ToastWithTextOnly
                            message={t("TOASTS.ERROR.VEHICLE_TYPE_CAR_NO_DATA")}/>,
                        {progress: undefined, type: "error"}
                    )
                }
            }
        }


        if (vehicleType === VEHICLE_TYPE.bus) {
            if ((totalPassengerCount ?? 0) > 0)
                totalPassengerCountError = (totalPassengerCount ?? 0) > maxTotalPassengerCount || (totalPassengerCount ?? 0) < minTotalPassengerCount

            if ((fuelConsumption === null || fuelConsumption === 0)) {
                if (busCategory === null) {
                    missingInformation = true
                    toast(<ToastWithTextOnly
                            message={t("TOASTS.ERROR.VEHICLE_TYPE_BUS_NO_DATA")}/>,
                        {progress: undefined, type: "error"}
                    )
                }
            } else {
                if (busCategory === null && (totalPassengerCount === null || totalPassengerCount === 0)) {
                    missingInformation = true
                    toast(<ToastWithTextOnly
                            message={t("TOASTS.ERROR.VEHICLE_TYPE_BUS_MISSING_DATA")}/>,
                        {progress: undefined, type: "error"}
                    )
                }

            }

        }

        setFormErrorData({
            acrissCodeError,
            manualEnteredDistanceError,
            calculatedDistanceError,
            fuelConsumptionError,
            daysCountError,
            personCountError,
            vehicleTypeError,
            totalPassengerCountError,
        })

        if (!missingInformation && !(
            acrissCodeError ||
            manualEnteredDistanceError ||
            calculatedDistanceError ||
            fuelConsumptionError ||
            daysCountError ||
            personCountError
        )) _calculateEmissions()
    }

    const _setFormErrorData = <K extends keyof FormErrorProps>(
        keyOrUpdates: K | Partial<FormProps>,
        payload?: FormErrorProps[K]
    ) => {
        setFormErrorData({
            ...formErrorData,
            ...(typeof keyOrUpdates === "string" ? {[keyOrUpdates]: payload} : keyOrUpdates)
        })
    }

    const _convertCarBusDataIntoRequestFormat = (): CarBusCalculationRequest => {
        let distanceKmValue: number | null = null
        let carInfoValue: CarBusCalculationRequest["carInfo"] = null
        let busCategoryValue = busCategory
        let countDaysValue: number | null = countDays

        if (manualEnteredDistanceKm !== null && manualEnteredDistanceKm > 0) {
            distanceKmValue = manualEnteredDistanceKm
        } else {
            if (calculatedDistanceKm !== null && calculatedDistanceKm > 0) {
                distanceKmValue = calculatedDistanceKm
            }
        }

        if (vehicleType === VEHICLE_TYPE.bus) {
            carInfoValue = null
            countDaysValue = null
        }

        if (vehicleType === VEHICLE_TYPE.car) {
            busCategoryValue = null
            let accrissCodeValue: AcrissCodeProps | null = null
            if (acrissCode !== null && acrissCode.length > 0) {
                accrissCodeValue = {
                    firstCharacter: acrissCode[0],
                    lastCharacter: acrissCode[acrissCode.length - 1],
                }
            }
            carInfoValue = {
                acrissCode: accrissCodeValue,
                carCategory,
            }
        }

        return _replaceZeroWithNull({
            ..._setCommonCarBusRequestProps(),
            vehicleType: vehicleType!!,
            numberOfPassengers: passengerCount,
            totalPassengerCount,
            fuelConsumption,
            fuelType,
            distance: distanceKmValue,
            daysOfUse: countDaysValue,
            carInfo: carInfoValue,
            busCategory,
            isHybrid,
        })
    }

    /**
     * The endpoint for the car calculator distinguishes between zero values (0) and null.
     */
    const _replaceZeroWithNull = <T extends CarBusCalculationRequest>(obj: T): T => Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, key === "index" ? value : value === 0 ? null : value])) as T

    const _calculateEmissions = () => {
        carBusForRequestFormatted.current = []
        setCalculationImprovementsHint(null)
        carBusForRequestFormatted.current = [_convertCarBusDataIntoRequestFormat()]
        try {
            calculateEmissions({carActivities: carBusForRequestFormatted.current})
        } catch (error) {
            console.error(`Something went wrong when calculating emissions for car or bus: ${error}`);
        }
    }

    const _setCommonCarBusRequestProps = () => ({
        index: 0,
        type: CALCULATION_TYPE.car,
    })

    const _setUnitOfFuelType = () => {
        const liter = t("GENERAL.LITER")
        if (fuelType === FUEL_TYPE.unknown) return liter
        if (fuelType === null) return NO_DATA_STRING
        if (fuelType === FUEL_TYPE.electric) return t("GENERAL.KW_H", {interpolation: {escapeValue: false}})
        return liter
    }

    const _renderResultString = (
        status: CalculatedEmissionsBackendResponse["status"],
        emissionKgCO2eTotal: CalculatedEmissionsBackendResponse["emissionKgCO2eTotal"]
    ) => <>
        <Text variant={"headlineMedium"}>
            {
                status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed ?
                    generateTotalCo2EAndCo2EPerPersonString(emissionKgCO2eTotal!, carBusForRequestFormatted.current[0].numberOfPassengers, resolvedLanguage, i18n) :
                    `${NO_DATA_STRING} ${KG_CO2}`
            }
        </Text>
        {calculationImprovementsHint &&
            <Text
                color={'warningLight'}>{calculationImprovementsHint}</Text>
        }
    </>

    const _renderResult = () => {
        if (resultCalculateEmissions && !hideCalculationResults) {
            const {status, emissionKgCO2eTotal} = resultCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        if (isGeneralCalculatedCarBusEmissionsResponse(errorCalculateEmissions)) {
            const {data: {status, emissionKgCO2eTotal}} = errorCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        return null
    }

    const _validateAcrissCode = (event: React.FocusEvent<HTMLInputElement>) => {
        const {target: {value: enteredAcrissCode}} = event
        if (enteredAcrissCode.length === 0) {
            _setFormErrorData("acrissCodeError", false)
        } else if (enteredAcrissCode.length > 2 || enteredAcrissCode.length <= 1) {
            _setFormErrorData("acrissCodeError", true)
        } else {
            if (enteredAcrissCode.length === 2) {
                acrissCodeForValidation.current = {
                    firstCharacter: enteredAcrissCode[0],
                    lastCharacter: enteredAcrissCode[1]
                }
                validateAcrissCode(acrissCodeForValidation.current)
            }
        }
    }

    const _setNumberInputDistanceKmValue = () => {
        if (manualEnteredDistanceKm !== null) return manualEnteredDistanceKm
        if (calculatedDistanceKm !== null) return calculatedDistanceKm
        return 0
    }

    const _setHybridOnSelectFuelType = (value: DropdownOption[] | null) => {
        if (value) {
            const selectedFuelType = value[0].raw.name
            if (selectedFuelType === "ELECTRIC") {
                setDisableIsHybridCheckbox(true)
                return false
            } else {
                setDisableIsHybridCheckbox(false)
            }
            return isHybrid
        }
        return isHybrid
    }

    const _generateIsHybridTooltip = () => {
        if (disableIsHybridCheckbox) {
            if (fuelType === FUEL_TYPE.electric) return "TOOLTIP.CAR_BUS.IS_HYBRID_DEACTIVATED_FUEL_TYPE"
            if (acrissCode !== null && _returnIsHybridValueOnAcrissCode(acrissCode[acrissCode.length - 1])) return "TOOLTIP.CAR_BUS.IS_HYBRID_DEACTIVATED_ACRISS_CODE"
        }
        return undefined
    }

    return <div className={"activity-form-calculate-container"}>
        <div
            style={{pointerEvents: "auto"}}
            ref={mapContainerRef}
            className={'choose-car-bus-route-map-container'}>
            {_renderCalculatedDistance()}
        </div>
        <div className={clsx("choose-car-bus-route-map-container", "choose-car-bus-route-map-container-overlay")}>
            <div
                className={clsx("choose-car-bus-route-waypoints-wrapper", "choose-car-bus-route-map-overlay")}>
                <Text variant={"headlineMedium"}>SCREENS.CAR_BUS.ROUTE</Text>
                {_renderTextInputWaypoints()}
            </div>
        </div>
        <div className={clsx('choose-car-bus-route-select-map-style', 'choose-car-bus-route-map-overlay')}>
            <RadioButtonGroup
                fieldsetLabel={"LABELS.CAR_BUS.MAP_STYLE"}
                onChange={selection => _setLayerSelection(selection)}
                radioButtons={[
                    {
                        label: "SCREENS.CAR_BUS.STREET",
                        value: STREET_LAYER_NUMBER
                    },
                    {
                        label: "SCREENS.CAR_BUS.SATELLITE",
                        value: SATELLITE_LAYER_NUMBER
                    },
                    {
                        label: "SCREENS.CAR_BUS.OUTDOOR",
                        value: OUTDOOR_LAYER_NUMBER
                    }
                ]}
                selected={selectedLayer}
                name={"SCREENS.CAR_BUS.MAP_STYLE"}
                orientation={"horizontal"}
                hideLabel={false}/>
        </div>
        <div
            className={"choose-car-bus-fieldset-wrapper"}
            onKeyDown={event => {
                if (event.key === "Enter") {
                    event.preventDefault()
                    _evaluateForm()
                }
            }}>
            <Fieldset
                fieldsetClasses={"choose-car-bus-fieldset-wrapper"}
                legend={"LABELS.CAR_BUS.ENTER_CAR_AND_ROUTE_INFORMATION"}
                showLegend={false}>
                <div
                    style={{marginTop: theme.spacer.large}}
                    className={"form-row"}>
                    <DropdownInput
                        formError={vehicleTypeError}
                        aria-errormessage={"ERRORS.CAR_BUS.VEHICLE_TYPE"}
                        containerStyle={{flex: 1}}
                        options={vehicleTypeDropdownOptions}
                        setSelection={selection => _setFormData({
                            vehicleType: selection ? selection[0].raw.name : null,
                            isHybrid: false,
                            carCategory: null,
                            busCategory: null,
                            fuelType: null,
                        })}
                        placeholder={"LABELS.CAR_BUS.VEHICLE_TYPE"}
                        label={"LABELS.CAR_BUS.VEHICLE_TYPE"}
                        required={true}
                        defaultValue={_getDefaultOption(vehicleTypeDropdownOptions, vehicleType)}/>
                    <DropdownInput
                        defaultValue={_getDefaultOption(fuelTypeDropdownOptions, fuelType)}
                        key={`fuelType-${vehicleType}`}
                        containerStyle={{flex: 1}}
                        options={fuelTypeDropdownOptions}
                        setSelection={value => _setFormData({
                            fuelType: value ? value[0].raw.name : null,
                            acrissCode: null,
                            isHybrid: _setHybridOnSelectFuelType(value)
                        })}
                        placeholder={"LABELS.CAR_BUS.FUEL_TYPE"}
                        label={"LABELS.CAR_BUS.FUEL_TYPE"}/>
                    <NumberInput
                        error={manualEnteredDistanceError}
                        aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                            min: CAR_BUS_PROPERTIES.minDistanceKm,
                            max: CAR_BUS_PROPERTIES.maxDistanceKm,
                            words: t("ERRORS.CAR_BUS.MIN_MAX_DISTANCE_EXCEEDED")
                        })}
                        required={fuelConsumption === null}
                        labelInputContainerStyles={{width: '100%'}}
                        containerStyle={{flex: 1}}
                        min={CAR_BUS_PROPERTIES.minDistanceKm}
                        max={CAR_BUS_PROPERTIES.maxDistanceKm}
                        injectMaximum={true}
                        injectMinimum={true}
                        onBlur={event => {
                            const {target: {valueAsNumber}} = event
                            _setFormData({manualEnteredDistanceKm: valueAsNumber});
                            if (valueAsNumber > 0) setCalculatedDistanceOverwritten(true)
                        }}
                        onChange={event => {
                            const {target: {valueAsNumber}} = event
                            _setFormData({manualEnteredDistanceKm: valueAsNumber});
                            if (valueAsNumber > 0) setCalculatedDistanceOverwritten(true)
                        }}
                        value={_setNumberInputDistanceKmValue()}
                        label={"LABELS.CAR_BUS.DISTANCE_KM"}/>
                    <NumberInput
                        error={fuelConsumptionError}
                        aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                            min: CAR_BUS_PROPERTIES.minFuelConsumptionL,
                            max: CAR_BUS_PROPERTIES.maxFuelConsumptionL,
                            words: t("ERRORS.CAR_BUS.MIN_MAX_FUEL_CONSUMPTION_EXCEEDED")
                        })}
                        required={(calculatedDistanceKm || manualEnteredDistanceKm) === null || (calculatedDistanceKm || manualEnteredDistanceKm) === 0}
                        labelInputContainerStyles={{width: '100%'}}
                        containerStyle={{flex: 1}}
                        min={CAR_BUS_PROPERTIES.minFuelConsumptionL}
                        max={CAR_BUS_PROPERTIES.maxFuelConsumptionL}
                        injectMaximum={true}
                        injectMinimum={true}
                        onBlur={event => {
                            const {target: {valueAsNumber}} = event
                            _setFormData("fuelConsumption", valueAsNumber)
                        }}
                        onChange={event => {
                            const {target: {valueAsNumber}} = event
                            _setFormData("fuelConsumption", valueAsNumber)
                        }}
                        value={fuelConsumption ?? 0}
                        label={t("LABELS.CAR_BUS.FUEL_CONSUMPTION", {
                            unit: _setUnitOfFuelType(),
                            interpolation: {escapeValue: false},
                        })}/>
                </div>
            </Fieldset>
            {vehicleType !== null &&
                <Fieldset
                    fieldsetClasses={"choose-car-bus-fieldset-wrapper"}
                    legend={"SCREENS.CAR_BUS.CAR_INFORMATION"}
                    required={true}
                    showLegend={true}>
                    <div className={"form-row"}>
                        <DropdownInput
                            key={`vehicleCategory-${resolvedLanguage}-${vehicleType}`}
                            containerStyle={{flex: 1}}
                            options={vehicleType === VEHICLE_TYPE.car ? carCategoryDropdownOptions : busCategoryDropdownOptions}
                            setSelection={selection => _setFormData({
                                [vehicleType === VEHICLE_TYPE.car ? "carCategory" : "busCategory"]: selection ? selection[0].raw.name : null,
                                acrissCode: null
                            })}
                            placeholder={"LABELS.CAR_BUS.CAR_BUS_CATEGORY"}
                            label={"LABELS.CAR_BUS.CAR_BUS_CATEGORY"}
                            required={true}
                            defaultValue={vehicleType === VEHICLE_TYPE.car ? _getDefaultOption(carCategoryDropdownOptions, carCategory) : _getDefaultOption(busCategoryDropdownOptions, busCategory)}/>
                        {vehicleType === VEHICLE_TYPE.car && <>
                            <TextInput
                                error={acrissCodeError}
                                aria-errormessage={"ERRORS.COMMON.CAR_BUS.INVALID_ACRISS_CODE"}
                                onBlur={_validateAcrissCode}
                                containerStyle={{width: '25%'}}
                                label={'LABELS.CAR_BUS.ACRISS_CODE'}
                                placeholder={"LABELS.CAR_BUS.FIRST_LAST_CHARACTER"}
                                onChange={event => {
                                    const {target: {value}} = event
                                    _setFormData("acrissCode", value.toUpperCase())
                                    if (value.length === 0) setDisableIsHybridCheckbox(false)
                                }}
                                value={acrissCode === null ? undefined : acrissCode}/>
                            <CheckboxInput
                                tooltip={_generateIsHybridTooltip()}
                                readOnly={disableIsHybridCheckbox}
                                label={"LABELS.CAR_BUS.HYBRID"}
                                checked={isHybrid}
                                onChange={() => _setFormData("isHybrid", !isHybrid)}
                            />
                        </>
                        }
                        {vehicleType === VEHICLE_TYPE.bus && <NumberInput
                            tooltip={"TOOLTIP.CAR_BUS.TOTAL_PASSENGER_COUNT"}
                            containerStyle={{flex: 1}}
                            readOnly={disableTotalPassengerCount}
                            min={CAR_BUS_PROPERTIES.minTotalPassengerCount}
                            max={CAR_BUS_PROPERTIES.maxTotalPassengerCount}
                            injectMaximum={true}
                            injectMinimum={true}
                            error={totalPassengerCountError}
                            aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                                min: CAR_BUS_PROPERTIES.minTotalPassengerCount,
                                max: CAR_BUS_PROPERTIES.maxTotalPassengerCount,
                                words: t("ERRORS.COMMON.MIN_MAX_TOTAL_PASSENGER_COUNT_EXCEEDED")
                            })}
                            onBlur={event => _setFormData("totalPassengerCount", event.target.valueAsNumber)}
                            onChange={event => _setFormData("totalPassengerCount", event.target.valueAsNumber)}
                            value={totalPassengerCount ?? 0}
                            label={"LABELS.CAR_BUS.TOTAL_PASSENGER_COUNT"}/>
                        }
                    </div>
                </Fieldset>
            }
            <Fieldset
                legend={"LABELS.CAR_BUS.DAYS_OF_USE_NUMBER_PASSENGERS"}
                showLegend={false}
                fieldsetStyles={{justifyContent: 'flex-start'}}
                fieldsetClasses={"form-row"}>
                <NumberInput
                    min={CAR_BUS_PROPERTIES.minDaysCount}
                    max={maxDaysCount.current}
                    injectMaximum={true}
                    injectMinimum={true}
                    onBlur={event => {
                        const {target: {valueAsNumber}} = event
                        _setFormData("countDays", valueAsNumber)
                    }}
                    onChange={event => {
                        const {target: {valueAsNumber}} = event
                        _setFormData("countDays", valueAsNumber)
                    }}
                    value={countDays}
                    error={daysCountError}
                    aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                        min: CAR_BUS_PROPERTIES.minDaysCount,
                        max: maxDaysCount.current,
                        words: t("ERRORS.CAR_BUS.MIN_MAX_DAYS_EXCEEDED")
                    })}
                    label={"LABELS.CAR_BUS.COUNT_DAYS"}/>
                <NumberInput
                    tooltip={totalPassengerCount !== null ? "TOOLTIP.CAR_BUS.PERSON_COUNT_CALCULATION_HINT" : undefined}
                    min={CAR_BUS_PROPERTIES.minPersonsCount}
                    max={maxPersonsCount.current}
                    injectMaximum={true}
                    injectMinimum={true}
                    error={personCountError}
                    aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                        min: CAR_BUS_PROPERTIES.minPersonsCount,
                        max: maxPersonsCount.current,
                        words: t("ERRORS.COMMON.MIN_MAX_PEOPLE_EXCEEDED")
                    })}
                    onBlur={event => {
                        const {target: {valueAsNumber}} = event
                        _setFormData("passengerCount", valueAsNumber)
                    }}
                    onChange={event => {
                        const {target: {valueAsNumber}} = event
                        _setFormData("passengerCount", valueAsNumber)
                    }}
                    value={passengerCount}
                    label={"LABELS.CAR_BUS.COUNT_PERSONS"}/>
            </Fieldset>
            <div className={'calculate-activity-bottom-buttons'}>
                {_renderResult()}
                <div className={'choose-flight-buttons-wrapper'}>
                    {!isEdit && <TextButton onClick={() => resetForm && resetForm()}>GENERAL.RESET</TextButton>}
                    <TextButton
                        disabled={disableCalculateButton}
                        onClick={_evaluateForm}>
                        {isEdit ? "GENERAL.CALCULATE_AGAIN" : "GENERAL.CALCULATE"}
                    </TextButton>
                </div>
            </div>

        </div>
        {isCalculatingEmissions && <DelayedSpinner fullscreen={true} delayMs={500}/>}
    </div>
}

export default ChooseCarBus